import { ReactNode, Suspense, useMemo } from 'react';
import { Loader } from '@givebutter/primitives/loader';
import * as Dialog from 'components/dialog';
import { DialogRouteContext } from 'components/dialog-route/DialogRouteContext';
import {
    DialogRouteProps as DialogRoutePropsPrimitive,
    isOpenFromMatch,
    useDialogRouteProps,
} from 'components/dialog-route/useDialogRouteProps';
import { Route as RoutePrimitive } from 'components/router/Route';

interface DialogRouteProps extends DialogRoutePropsPrimitive {
    contentProps?: Dialog.DialogContentProps;
}

const DialogRoute = (props: DialogRouteProps) => {
    const { contentProps, ...rest } = props;
    const { variant, ...restContentProps } = contentProps ?? {};

    const { path, onClose, Component, component, children, ...routeProps } =
        useDialogRouteProps(rest);

    const providerProps = useMemo(() => ({ goBack: onClose }), [onClose]);
    const ResolvedComponent = component ? <Component {...props} /> : null;
    const DynamicChild = children ? (children as ReactNode) : ResolvedComponent;

    return (
        <DialogRouteContext.Provider value={providerProps}>
            <RoutePrimitive path={path} {...routeProps}>
                {props => {
                    const isOpen = isOpenFromMatch(props.match, path);

                    return (
                        <Dialog.Root
                            open={isOpen}
                            onOpenChange={open => {
                                if (!open) {
                                    onClose();
                                }
                            }}
                        >
                            <Dialog.Portal>
                                {variant !== 'fullscreen' && <Dialog.Overlay />}
                                <Dialog.Content variant={variant} {...restContentProps}>
                                    <Suspense fallback={<DialogSkeleton />}>
                                        {isOpen ? DynamicChild : <DialogSkeleton />}
                                    </Suspense>
                                </Dialog.Content>
                            </Dialog.Portal>
                        </Dialog.Root>
                    );
                }}
            </RoutePrimitive>
        </DialogRouteContext.Provider>
    );
};

const DialogSkeleton = () => (
    <>
        <Dialog.Title className="sr-only">Loading...</Dialog.Title>
        <Dialog.Body className={'h-[500px] w-[500px]'}>
            <Loader />
        </Dialog.Body>
    </>
);

const Route = DialogRoute;
const Skeleton = DialogSkeleton;

export {
    DialogRoute,
    DialogSkeleton,
    //
    Route,
    Skeleton,
};
